import React, { useState, useRef } from "react";
import { GripVertical, Trash2, AlertCircle } from "lucide-react";

/**
 * A card component representing a judging criterion with drag-and-drop support
 */
export function CriterionCard({
  criterion,
  index,
  onChange,
  onRemove,
  onReorder,
  isPercentageMode,
  draggedIndex = null, // Index of currently dragged item
  onDragHover = null, // Callback for drag hover events
  onDragStart = null, // Callback when drag starts
  onDragEnd = null, // Callback when drag ends
}) {
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverPosition, setDragOverPosition] = useState(null); // 'top' or 'bottom'
  const dragRef = useRef(null);

  const handlePointsChange = (rawValue) => {
    if (rawValue === "") {
      onChange(criterion.id, "maxPoints", "");
      setError("");
      return;
    }
    const parsed = parseFloat(rawValue);
    const value = Number.isNaN(parsed) ? "" : parsed;

    if (value > 100) {
      setError(`Maximum 100 ${isPercentageMode ? "%" : "points"} allowed`);
    } else if (value < 0) {
      setError(
        `${isPercentageMode ? "Percentage" : "Points"} cannot be negative`
      );
    } else {
      setError("");
    }
    onChange(criterion.id, "maxPoints", value < 0 ? "" : Math.min(value, 100));
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData("text/plain", index.toString());
    e.dataTransfer.effectAllowed = "move";
    
    // Add a drag preview positioned relative to where the user clicked
    if (dragRef.current) {
      const rect = dragRef.current.getBoundingClientRect();
      // Calculate the offset from the click position to the card's top-left corner
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      e.dataTransfer.setDragImage(dragRef.current, offsetX, offsetY);
    }
    
    // Notify parent component
    if (onDragStart) {
      onDragStart(index);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDragOverPosition(null);
    
    // Notify parent component
    if (onDragEnd) {
      onDragEnd();
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      // Determine if we're in the top or bottom half of the card
      // Use a more generous detection area
      const rect = dragRef.current.getBoundingClientRect();
      const cardCenter = rect.top + rect.height / 2;
      const buffer = rect.height * 0.2; // 20% buffer zone
      
      let position;
      if (e.clientY < cardCenter - buffer) {
        position = 'top';
      } else if (e.clientY > cardCenter + buffer) {
        position = 'bottom';
      } else {
        // In the middle zone, keep current position or default to top
        position = dragOverPosition || 'top';
      }
      
      setDragOverPosition(position);
      
      // Notify parent component for real-time reordering
      if (onDragHover) {
        const targetIndex = position === 'top' ? index : index + 1;
        onDragHover(draggedIndex, targetIndex);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    
    if (draggedIndex !== null && draggedIndex !== index) {
      // Update position based on mouse location within the card
      // Use the same generous detection as dragEnter
      const rect = dragRef.current.getBoundingClientRect();
      const cardCenter = rect.top + rect.height / 2;
      const buffer = rect.height * 0.2; // 20% buffer zone
      
      let position;
      if (e.clientY < cardCenter - buffer) {
        position = 'top';
      } else if (e.clientY > cardCenter + buffer) {
        position = 'bottom';
      } else {
        // In the middle zone, keep current position or default to top
        position = dragOverPosition || 'top';
      }
      
      if (position !== dragOverPosition) {
        setDragOverPosition(position);
        
        // Notify parent component for real-time reordering
        if (onDragHover) {
          const targetIndex = position === 'top' ? index : index + 1;
          onDragHover(draggedIndex, targetIndex);
        }
      }
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if we're actually leaving the card (not just moving to a child element)
    if (!dragRef.current?.contains(e.relatedTarget)) {
      setDragOverPosition(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOverPosition(null);
    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (dragIndex === index) return;
    
    // Determine final drop position using the same generous detection
    const rect = dragRef.current.getBoundingClientRect();
    const cardCenter = rect.top + rect.height / 2;
    const buffer = rect.height * 0.2; // 20% buffer zone
    
    let position;
    if (e.clientY < cardCenter - buffer) {
      position = 'top';
    } else if (e.clientY > cardCenter + buffer) {
      position = 'bottom';
    } else {
      // In the middle zone, default to top
      position = 'top';
    }
    
    const targetIndex = position === 'top' ? index : index + 1;
    onReorder(dragIndex, targetIndex);
  };

  const isBeingDragged = draggedIndex === index;
  const isDropTarget = draggedIndex !== null && draggedIndex !== index && dragOverPosition !== null;

  return (
    <div className="relative">
      {/* Drop indicator line */}
      {isDropTarget && dragOverPosition === 'top' && (
        <div className="absolute -top-1 left-0 right-0 h-0.5 bg-primary rounded-full z-10 shadow-lg" />
      )}
      
      <div
        ref={dragRef}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`card bg-base-100 shadow-sm hover:shadow-md transition-all duration-200 ${
          isBeingDragged ? "opacity-50 scale-105 shadow-lg" : ""
        } ${
          isDropTarget ? "ring-2 ring-primary ring-opacity-50" : ""
        }`}
      >
      <div className="card-body p-4">
        <div className="flex items-center gap-4">
          <div className="cursor-move p-1 hover:bg-base-200 rounded transition-colors">
            <GripVertical className="h-5 w-5 opacity-50 hover:opacity-100" />
          </div>

          <div className="flex-1 flex items-center gap-4">
            <input
              type="text"
              value={criterion.label}
              onChange={(e) => onChange(criterion.id, "label", e.target.value)}
              placeholder="Criterion name (e.g., Technical Skill)"
              className="input input-bordered input-sm flex-1"
            />
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={criterion.maxPoints}
                onChange={(e) => handlePointsChange(e.target.value)}
                className={`input input-bordered input-sm w-20 text-right ${
                  error ? "input-error" : ""
                }`}
              />
              <span className="text-sm opacity-70 whitespace-nowrap">
                {isPercentageMode ? "% weight" : "points max"}
              </span>
            </div>
          </div>

          <button
            onClick={() => onRemove(criterion.id)}
            className="btn btn-ghost btn-sm btn-square text-error"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        {error && (
          <div className="text-xs text-error flex items-center gap-1 mt-1 ml-10">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )}
      </div>
      
      {/* Bottom drop indicator line */}
      {isDropTarget && dragOverPosition === 'bottom' && (
        <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full z-10 shadow-lg" />
      )}
    </div>
    </div>
  );
} 